import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

// api / model
import DepartmentsApi from '../../api/departments.api';
import { useAppSelector } from '../../hooks/hooks';
import Department from '../../models/department';

// comps
import { Box, Typography, TableCell, CircularProgress } from '@mui/material';
import DataGrid from '../../components/grid/dataGrid';
import { CustomTableCheckboxCell } from '../../components/grid/CustomTableCell';
import FilterContainer from '../../components/layout/filterContainer';
import ContentContainer from '../../components/layout/contentContainer';
import FilterOption from '../../components/global/filterOption';
import GlobalButton from '../../components/global/button';

// icons
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';
import GlobalInput from '../../components/global/input';

// constants
import { defaultFilerOptionsArr } from '../../constants';
import { colors } from '../../theme';

const Programs = (props: any) => {
    const appState = useAppSelector((state) => state.appState);
    const { school, term } = appState;
    const _departmentsApi = new DepartmentsApi();

    const [state, setState] = useState({
        filterObject: {
            programCode: null,
            isPublic: 'all',
            isDraft: 'all',
        },
        programData: [],
        isClearAll: false,
    });
    const [resetPage, setResetPage] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const loadProgramData = async (schoolId: number, termId: number) => {
        const programs = await _departmentsApi.GetDepartmentsBySchoolTerm(schoolId, termId);
        setState({
            ...state,
            programData: programs,
        });
        setIsLoading(true);
    };

    useEffect(() => {
        if (school?.id && term?.termId)
            loadProgramData(school.id, term.termId)
                .catch((error) => toast.error('Error loading programs'))
                .finally(() => setIsLoading(false));
    }, [appState]);

    const handleFilter = (name: any, value: any) => {
        setState({
            ...state,
            filterObject: {
                ...state.filterObject,
                [name]: name === 'programCode' ? value.toUpperCase() : value,
            },
        });
        setButtonDisabled(false);
    };

    const handleClearAll = () => {
        setState({
            ...state,
            isClearAll: true,
            filterObject: {
                programCode: '',
                isPublic: 'all',
                isDraft: 'all',
            },
        });
        setResetPage(true);
        setButtonDisabled(true);
    };

    const getGridData = () => {
        let programData = [...state.programData];
        const { programCode, isPublic, isDraft } = state.filterObject;

        if (programCode)
            programData = programData.filter(
                (program) => program.departmentCode.toLowerCase().includes(programCode.toLowerCase()) || program.departmentName.toLowerCase().includes(programCode.toLowerCase()),
            );
        if (isPublic !== 'all') programData = programData.filter((program) => program.isOpen === isPublic);
        if (isDraft !== 'all') programData = programData.filter((program) => program.isDraft === isDraft);

        return programData;
    };

    useEffect(() => {
        if (!Object.values(state.filterObject).every((o) => o === '')) {
            setResetPage(true);
        } else {
            setResetPage(false);
            setButtonDisabled(true);
        }
    }, [state.filterObject]);

    useEffect(() => {
        setState({ ...state, isClearAll: false });
    }, [state.isClearAll]);

    const updateProgram = async (program: Department) : Promise<boolean> => {
        try {
            await _departmentsApi.UpdateDepartmentTerm(program);
            await loadProgramData(school.id, term.termId);
            toast.success(`Changes have been successfully saved.`);
            return true;
        } catch (error) {
            toast.error("Error updating program");
            return false
        }
    };

    const renderProgramGrid = (data: any, onChange: any) => {
        const { departmentCode, departmentName } = data;

        return (
            <>
                <TableCell sx={{ width: '10%' }}>{departmentCode}</TableCell>
                <TableCell sx={{ width: '30%' }}>{departmentName}</TableCell>
                <CustomTableCheckboxCell {...{ data, name: 'isOpen', onChange, disabled: false, width: '20%' }} />
                <CustomTableCheckboxCell {...{ data, name: 'isDraft', onChange, disabled: false, width: '20%' }} />
            </>
        );
    };

    return (
        <>
            <FilterContainer sx={{ justifyContent: 'space-between' }}>
                <FlexBox>
                    <GlobalInput
                        label="filter"
                        type="text"
                        name={'programCode'}
                        placeholder="Search by program code or name"
                        icon={<FilterIcon />}
                        sx={{ minWidth: '240px' }}
                        onChange={handleFilter}
                        value={state.filterObject?.programCode}
                    />
                    <FilterOptionLabel variant="body2">Public</FilterOptionLabel>
                    <FilterOption
                        data={defaultFilerOptionsArr}
                        state={state.filterObject.isPublic}
                        name={'label'}
                        onClickValue={'value'}
                        onOptionClick={(value: any) => handleFilter('isPublic', value)}
                    />
                    <FilterOptionLabel variant="body2">Draft</FilterOptionLabel>
                    <FilterOption
                        data={defaultFilerOptionsArr}
                        state={state.filterObject.isDraft}
                        name={'label'}
                        onClickValue={'value'}
                        onOptionClick={(value: any) => handleFilter('isDraft', value)}
                    />
                </FlexBox>
                <Box sx={{ width: '15%' }}>
                    <GlobalButton variant="round" text="clear all" fullWidth secondary={'true'} endIcon={<RefreshIcon />} onClick={handleClearAll} disabled={buttonDisabled} />
                </Box>
            </FilterContainer>
            {state.programData?.length === 0 && isLoading ? (
                <ContentContainer padding={'0px 25px'}>
                    <FlexBox sx={{ justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={100} />
                    </FlexBox>
                </ContentContainer>
            ) : (
                <DataGrid
                    isEdit
                    topHeight={12}
                    data={getGridData()}
                    columns={programGridColumns}
                    renderRows={renderProgramGrid}
                    onSaveAsync={updateProgram}
                    dataItemKey="departmentID"
                    isClearSorting={state.isClearAll}
                    resetPage={resetPage}
                    onHeaderClick={() => {
                        setButtonDisabled(false);
                    }}
                />
            )}
        </>
    );
};

export default Programs;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
        & > * {
            margin-right: 10px;
        }
    }
`;

const FilterOptionLabel = styled(Typography)`
    && {
        color: ${colors.grey.dark};
        font-weight: 700;
    }
`;

const programGridColumns = [
    {
        id: 'departmentCode',
        label: 'Program',
    },
    {
        id: 'departmentName',
        label: 'Name',
    },
    {
        label: 'Open for Public',
        sx: {
            textAlign: 'center',
        },
        isTooltip: true,
        tooltipText: 'Show/Hide program from public site',
        disabled: true,
    },
    {
        label: 'Marked as Draft',
        sx: {
            textAlign: 'center',
        },
        isTooltip: true,
        tooltipText: 'When checked: program is not finalized',
        disabled: true,
    },
];
